import {
  selectAccountExtraInfoGeneralPrecision,
  selectAccountExtraInfoGraphTotalData,
} from "@shared/slices/portfolio.slice";
import AccountChart from "common/Charts/LineChart/AccountChart";
import React, { useContext, useEffect, useMemo } from "react";
import useMemoizedSelector from "@shared/utils/hooks/useMemoizedSelector";
import BalanceStatistic from "common/Statistic/BalanceStatistic";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { processAccountExtraInfoGeneralGraphData } from "@shared/features/portfolio/utils";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { accountExtraDetailsFilters } from "@shared/slices/filters.slice";
import { ClientData, GetAccountExtraInfoGeneralRequest } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import AccountInfo from "features/configuration/accounts/AccountConfigurationDrawer/AccountInfo";
import AccountConfiguration from "features/configuration/accounts/AccountConfigurationDrawer/AccountConfiguration/AccountConfiguration";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { ExtraDetailsDrawerContext } from "common/ExtraDetails/ExtraDetailsDrawer.context.ts";
import { ACCOUNT_EXTRA_DETAILS_TABS } from "common/constants.ts";
import { skipToken, SkipToken } from "@reduxjs/toolkit/query";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import ExposureCard from "features/portfolio/AccountDrawer/ExposureCard.tsx";
import { MARGIN_EXCHANGES } from "@shared/common/constants.ts";
import styled, { useTheme } from "styled-components";
import { useDispatch } from "react-redux";
import { LanguageNameSpaces, TimeRange } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { Panel } from "common/NewLook";
import NewLookBalanceHistory from "features/portfolio/NewLook/NewLookBalanceHistory.tsx";
import { toBalancePoints } from "features/portfolio/NewLook/NewLookPortfolioChange.tsx";

interface GeneralInfoProps {
  args: GetAccountExtraInfoGeneralRequest | SkipToken;
  accountData?: ClientData;
}

const GeneralInfo = ({ args, accountData }: GeneralInfoProps) => {
  const { setTabQueries } = useContext(ExtraDetailsDrawerContext) || {};
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const query =
    sharedLib.sharedApi.portfolio.useGetAccountExtraInfoGeneralQuery(args);
  const { data, isLoading, isFetching, isSuccess, currentData } = query;
  const { isReadOnly } = useSubUserPermissions();
  const { isNewLook } = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const dispatch = useDispatch();

  useEffect(() => {
    setTabQueries?.(ACCOUNT_EXTRA_DETAILS_TABS.GENERAL, [query]);
  }, [query]);

  const displayCurrency = useSelector(selectDisplayCurrency);
  const graphData = useMemo(
    () =>
      args !== skipToken
        ? processAccountExtraInfoGeneralGraphData(
            data,
            args.time_range,
            displayCurrency
          )
        : [],
    [data]
  );

  const balance = useMemoizedSelector<number>({
    selectorCreator: selectAccountExtraInfoGraphTotalData,
    args,
  });

  const precisionSelector = useMemo(() => {
    return selectAccountExtraInfoGeneralPrecision(args);
  }, [args]);

  const chartStyle = useMemo(() => {
    return { height: isBottomNavigator ? 275 : 375 };
  }, [isBottomNavigator]);
  const balancePoints = useMemo(() => toBalancePoints(graphData), [graphData]);

  if (isNewLook)
    return (
      <NewLookStack>
        <Panel flush={true}>
          <BalanceZone>
            <NewLookBalanceHistory
              data-testid="account-balance-history"
              points={balancePoints}
              timeRange={
                (args !== skipToken ? args.time_range : "1d") as TimeRange
              }
              onTimeRangeChange={(range) =>
                dispatch(
                  accountExtraDetailsFilters.actions.setTimeRangeFilter(range)
                )
              }
              loading={isFetching}
              balance={data ? balance : undefined}
              title={t("overview.balance")}
              chartHeight={isBottomNavigator ? 160 : 200}
            />
          </BalanceZone>
        </Panel>
        {accountData && <AccountInfo account={accountData} />}
        {accountData?.exchange &&
          MARGIN_EXCHANGES.has(accountData?.exchange) && (
            <ExposureCard data={data?.data} />
          )}
        {!isReadOnly && (
          <AccountConfiguration accountId={accountData?.client_id} />
        )}
      </NewLookStack>
    );

  return (
    <LoadingSpinnerOverlay
      isLoading={!currentData}
      hideContentWhileLoading={true}
      spinnerSize="large"
    >
      <BalanceStatistic
        balance={balance}
        precisionSelector={precisionSelector}
      />
      <AccountChart
        filters={accountExtraDetailsFilters}
        data={graphData}
        precisionSelector={precisionSelector}
        autoLayoutHeight={false}
        loading={!isLoading && isFetching}
        isSuccess={isSuccess}
        style={chartStyle}
      />
      {accountData && (
        <div style={{ marginTop: 16 }}>
          <AccountInfo account={accountData} />
        </div>
      )}
      {accountData?.exchange && MARGIN_EXCHANGES.has(accountData?.exchange) && (
        <div style={{ marginTop: 16 }}>
          <ExposureCard data={data?.data} />
        </div>
      )}
      {!isReadOnly && (
        <div style={{ marginTop: 16 }}>
          <AccountConfiguration accountId={accountData?.client_id} />
        </div>
      )}
    </LoadingSpinnerOverlay>
  );
};

export default GeneralInfo;

const NewLookStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const BalanceZone = styled.div`
  padding: 20px 20px 16px 24px;
  @media (max-width: 600px) {
    padding: 16px;
  }
`;
